import { ConfigConstants } from '../../config/config-constants';
import { ModelOptions } from './model-options';
const rowAmount = 3;
const columnAmount = 1000;

/**
 * Configure the behaviour of a TextModel
 */
export class MultilineTextModelOptions extends ModelOptions {
    public readonly minLength: number;
    public readonly maxLength: number;
    public readonly lineLength: number;
    public readonly rows: number;
    public readonly columns: number;
    public readonly placeholder: string;
    public readonly pattern: string;
    public readonly patternScan: string;
    public readonly patternErrorMessage?: string;
    public readonly required: boolean;
    public readonly scannable: boolean;
    public readonly readonly: boolean;
    public readonly unit: string;
    // IN, AU and NZ - .{1,90}(?:\s+|\s*$)
    public readonly formatMultilineTextPattern: string;
    public readonly maxLines: number;
    public readonly applicationIdentifier: ConfigConstants.ApplicationIdentifier;
    // In P58, In Internal Info screen, Split line in Notes field is not required as backend accepts only single line
    public readonly splitLines: boolean;

    /**
     * Constructor that expects an object with some or all of the supported options set
     *
     * @param {number} [minLength]
     * @param {number} [maxLength]
     * @param {number} [lineLength]
     * @param {number} [rows]
     * @param {number} [columns]
     * @param {string} [placeholder]
     * @param {string} [pattern] String representing a regex pattern for validation
     * @param {string} [patternScan] String representing a regex pattern for barcode scanning
     * @param {string} [patternErrorMessage] translation key of a field specific message shown if provided, when the pattern check fails.
     * @param {boolean} [required] is this parameter mandatory?
     * @param {boolean} [scannable] should the barcode scanner be available
     * @param {boolean} [readonly] should the input field be readonly?
     * @param {string} [formatMultilineTextPattern] pattern to format multiline text area
     * @param {number} [maxLines] maximum number of lines allowed
     * @param {ConfigConstants.ApplicationIdentifier} [applicationIdentifier] are numeric prefixes used in barcode scan to define the meaning and format of encoded data elements.
     * @param {boolean} [splitLines] is split lines required if line ends with "\n"?
     */
    public constructor({
        minLength = 0,
        maxLength = Infinity,
        lineLength = ConfigConstants.defaultLineLength,
        rows = rowAmount,
        columns = columnAmount,
        placeholder = '',
        pattern = '[\\s\\S]*',
        patternScan = '[\\s\\S]*',
        patternErrorMessage,
        required = false,
        readonly = false,
        scannable = false,
        formatMultilineTextPattern = '',
        maxLines = Infinity,
        applicationIdentifier = ConfigConstants.ApplicationIdentifier.Default,
        splitLines = true,
    }: {
        minLength?: number;
        maxLength?: number;
        lineLength?: number;
        rows?: number;
        columns?: number;
        placeholder?: string;
        pattern?: string;
        patternScan?: string;
        patternErrorMessage?: string;
        required?: boolean;
        readonly?: boolean;
        scannable?: boolean;
        formatMultilineTextPattern?: string;
        maxLines?: number;
        applicationIdentifier?: ConfigConstants.ApplicationIdentifier;
        splitLines?: boolean;
    } = {}) {
        super();
        this.minLength = minLength;
        this.maxLength = maxLength;
        this.lineLength = lineLength;
        this.rows = rows;
        this.columns = columns;
        this.placeholder = placeholder;
        this.pattern = pattern;
        this.patternScan = patternScan;
        this.patternErrorMessage = patternErrorMessage;
        this.required = required;
        this.readonly = readonly;
        this.scannable = scannable;
        this.formatMultilineTextPattern = formatMultilineTextPattern;
        this.maxLines = maxLines;
        this.applicationIdentifier = applicationIdentifier;
        this.splitLines = splitLines;
    }
}

export class MultilineTextModelOptionsFromJson extends MultilineTextModelOptions {
    public constructor(data: JsonMultilineTextModelOptions) {
        super({
            minLength: data.minLength,
            maxLength: data.maxLength ? data.maxLength : Infinity,
            lineLength: data.lineLength ? data.lineLength : ConfigConstants.defaultLineLength,
            rows: data.rows,
            columns: data.columns,
            placeholder: data.placeholder,
            pattern: data.pattern,
            patternScan: data.patternScan,
            required: data.required,
            readonly: data.readonly,
            scannable: data.scannable,
            formatMultilineTextPattern: data.formatMultilineTextPattern,
            maxLines: data.maxLines ? data.maxLines : Infinity,
            applicationIdentifier: data.applicationIdentifier,
            splitLines: data.splitLines,
        });
    }
}

export interface JsonMultilineTextModelOptions {
    minLength: number;
    maxLength: number;
    lineLength: number;
    rows: number;
    columns: number;
    placeholder: string;
    pattern: string;
    patternScan: string;
    required: boolean;
    scannable: boolean;
    readonly: boolean;
    visible: boolean;
    formatMultilineTextPattern: string;
    maxLines: number;
    applicationIdentifier: ConfigConstants.ApplicationIdentifier;
    splitLines: boolean;
}
